import React, { useCallback, useEffect, useState } from 'react';
import socket from '../socket';
import useSocketEvent from '../utils/useSocketEvent';
import { fetchJson } from '../utils/api';
import Button from './ui/Button';

const CLUE_COUNT = 3;

function formatAction(action) {
    if (!action) return '';
    switch (action.type) {
        case 'check': return 'checkt';
        case 'fold':  return 'foldet';
        case 'call':  return `callt ${action.amount}`;
        case 'raise': return `raiset auf ${action.amount}`;
        case 'allin': return `All-In ${action.amount > 0 ? `(${action.amount})` : ''}`.trim();
        case 'ante':  return `Ante ${action.amount}`;
        case 'blind': return `Blind ${action.amount}`;
        default:      return action.type;
    }
}

// Live-Steuerung (Moderator) für das Quizpoker-Finale.
function FinaleQuizpokerRegie() {
    const [finale, setFinale] = useState(null);
    const [teams, setTeams] = useState([]);
    const [showCorrections, setShowCorrections] = useState(false);
    // Frage-Auswahl: lokal verwalten, nicht mehr direkt an Server senden
    const [selectedHandIndex, setSelectedHandIndex] = useState(0);
    // Korrekturen-State
    const [answerInputs, setAnswerInputs] = useState({});
    const [betInputs, setBetInputs] = useState({});
    const [potInput, setPotInput] = useState('');
    const [raiseInputs, setRaiseInputs] = useState({});

    const loadTeams = useCallback(async () => {
        try {
            const data = await fetchJson('/api/teams');
            setTeams(Array.isArray(data) ? data.filter((t) => (t.name || '').toLowerCase() !== 'moderator') : []);
        } catch (_) { /* ignore */ }
    }, []);

    const register = useCallback(() => {
        socket.emit('finale:registerControl');
        socket.emit('finale:state:request');
    }, []);

    useSocketEvent(socket, 'connect', register);
    useSocketEvent(socket, 'teamsUpdated', loadTeams);
    useSocketEvent(socket, 'finale:state', useCallback(({ finale: f }) => {
        if (f?.kind === 'quizpoker') {
            setFinale(f);
            // Lokalen Index nur synchronisieren wenn keine Hand läuft
            if (!f.live) {
                setSelectedHandIndex(f.handIndex ?? 0);
            }
        }
    }, []));

    useEffect(() => {
        loadTeams();
        register();
        return () => socket.emit('finale:unregisterControl');
    }, [loadTeams, register]);

    const emit = (event, payload) => socket.emit(event, payload);
    const teamName = useCallback((id) => {
        const t = teams.find((x) => String(x.id) === String(id));
        return t ? t.name : `Team ${id}`;
    }, [teams]);

    if (!finale) {
        return (
            <div className="card section-stack qp-regie">
                <div className="text-muted">Regie wird geladen…</div>
            </div>
        );
    }

    const live = finale.live || null;
    const phase = live?.phase || null;
    const hands = finale.hands || [];
    const handIndex = finale.handIndex ?? 0;
    const active = !!finale.active;
    const revealedClues = live?.revealedClues ?? 0;
    const roundComplete = !!live && phase === 'betting' && live.actingTeamId == null;
    const perTeamIds = live ? Object.keys(live.perTeam) : [];
    const currentHand = hands[handIndex] || null;
    const canNavigate = hands.length > 0;
    const handHasImage = (currentHand?.attachments || []).some((a) => (a?.mime || '').startsWith('image/'));

    // Split-Pot-Informationen (für resolved-Phase)
    const payoutEntries = Object.entries(live?.payoutDisplay || {});
    const isSplitPot = payoutEntries.length > 1;

    // ── Primäre Aktion: ein einziger progressiver Button ──────────────────────
    // Sequenz: Hand starten → Frage anzeigen → Setzrunde starten →
    //          Hinweis anzeigen → (Setzrunde) → … → Zum Showdown →
    //          Auflösen & Pot vergeben → Nächste Frage
    const getPrimaryAction = () => {
        if (!live) {
            return {
                label: 'Hand starten',
                onClick: () => emit('quizpoker:startHand', { handIndex: selectedHandIndex }),
                disabled: !hands.length,
            };
        }
        if (phase === 'answering') {
            if (!live.questionVisible) {
                return {
                    label: 'Frage anzeigen',
                    onClick: () => emit('quizpoker:setVisible', { contentType: 'question', visible: true }),
                };
            }
            return {
                label: 'Setzrunde starten',
                onClick: () => emit('quizpoker:beginBetting'),
            };
        }
        if (phase === 'betting') {
            // Während Teams noch setzen: kein primärer Button — "Setzrunde überspringen" übernimmt
            if (!roundComplete) return null;
            if (revealedClues < CLUE_COUNT) {
                return { label: 'Hinweis anzeigen', onClick: () => emit('quizpoker:revealClue') };
            }
            return { label: 'Zum Showdown', onClick: () => emit('quizpoker:showdown') };
        }
        if (phase === 'showdown') {
            return { label: 'Auflösen & Pot vergeben', onClick: () => emit('quizpoker:resolve') };
        }
        if (phase === 'resolved') {
            return {
                label: 'Hand starten',
                onClick: () => emit('quizpoker:jumpToHand', { handIndex: selectedHandIndex }),
                disabled: !hands.length,
            };
        }
        return null;
    };
    const primaryAction = getPrimaryAction();

    const renderPot = () => {
        if (!live) return null;
        if (phase === 'resolved' && isSplitPot) {
            return (
                <div className="qp-regie-pot">
                    <span className="qp-regie-pot-label">Split Pot</span>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {payoutEntries.map(([id, gain]) => (
                            <div key={id} style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                                <span style={{ color: 'var(--muted)', fontSize: 13 }}>{teamName(id)}</span>
                                <span className="qp-regie-pot-value" style={{ fontSize: 22 }}>+{gain}</span>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }
        return (
            <div className="qp-regie-pot">
                <span className="qp-regie-pot-label">Pot</span>
                <span className="qp-regie-pot-value">{live.pot}</span>
            </div>
        );
    };

    return (
        <div className="card section-stack qp-regie">
            <div className="card-header">
                <h3 className="card-title">Regie</h3>
                <button
                    type="button"
                    className={`toggle-button ${active ? 'is-on' : ''}`}
                    role="switch"
                    aria-checked={active}
                    onClick={() => emit('quizpoker:toggle', { active: !active })}
                >
                    <span className="toggle-indicator" aria-hidden="true" />
                    <span className="toggle-label">Finale aktiv</span>
                </button>
            </div>

            {/* Status-Zeile */}
            <div className="qp-regie-status text-muted">
                Frage {hands.length ? handIndex + 1 : 0} / {hands.length}
                {live && <> · {phase} · Hinweise {revealedClues}/{CLUE_COUNT}</>}
            </div>

            {/* Prominente Pot-Anzeige */}
            {renderPot()}

            {/* ── Primärer Aktions-Button ── */}
            {primaryAction && (
                <div className="button-row">
                    <Button
                        variant="primary"
                        disabled={!!primaryAction.disabled}
                        onClick={primaryAction.onClick}
                    >
                        {primaryAction.label}
                    </Button>
                </div>
            )}

            {/* Navigation: Frage wählen + Hand abbrechen */}
            {canNavigate && (
                <div className="qp-regie-nav section-stack">
                    <div className="qp-regie-nav-row button-row">
                        <select
                            className="qp-nav-select"
                            value={selectedHandIndex}
                            onChange={(e) => setSelectedHandIndex(Number(e.target.value))}
                        >
                            {hands.map((h, i) => (
                                <option key={h.id || i} value={i}>
                                    Frage {i + 1}{h.question ? `: ${h.question.substring(0, 40)}${h.question.length > 40 ? '…' : ''}` : ''}
                                </option>
                            ))}
                        </select>
                        {live && (
                            <Button size="sm" variant="danger" onClick={() => emit('quizpoker:resetLive')}>
                                Hand abbrechen
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {/* Hinweis-Vorschau für den Moderator */}
            {currentHand && currentHand.clues?.length > 0 && (
                <div className="qp-regie-clue-preview">
                    {currentHand.clues.map((clue, i) => {
                        const isVisible = i < revealedClues;
                        return (
                            <div key={i} className={`qp-regie-clue-item ${isVisible ? 'visible' : 'hidden'}`}>
                                <span className="qp-regie-clue-no">{i + 1}</span>
                                <span className="qp-regie-clue-text">{clue || <em>—</em>}</span>
                                <span className={`qp-regie-clue-badge ${isVisible ? 'visible' : 'hidden'}`}>
                                    {isVisible ? 'Sichtbar' : 'Verborgen'}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ── Bild-Platzierung: immer sichtbar während eine Hand läuft und ein Bild vorhanden ist ── */}
            {live && handHasImage && (
                <div className="button-row">
                    {[
                        { key: 'below', label: 'Bild unten' },
                        { key: 'right', label: 'Bild rechts' },
                        { key: 'fullscreen', label: 'Vollbild' },
                    ].map(({ key, label }) => {
                        const isActive = live.imageVisible && live.imagePlacement === key;
                        return (
                            <Button
                                key={key}
                                size="sm"
                                variant={isActive ? 'primary' : 'surface'}
                                onClick={() => emit('quizpoker:setImagePlacement', { placement: isActive ? null : key })}
                            >
                                {label}
                            </Button>
                        );
                    })}
                </div>
            )}

            {/* ── Schätzrunde: Team-Status ── */}
            {phase === 'answering' && (
                <div className="section-stack">
                    <div className="qp-regie-teams">
                        {perTeamIds.map((id) => (
                            <span key={id} className="chip">
                                {teamName(id)}: {live.perTeam[id].answered
                                    ? `✓ ${live.perTeam[id].answer ?? '—'}`
                                    : 'offen'}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Setzrunde: Status + Log + sekundäre Kontrollen ── */}
            {phase === 'betting' && (
                <div className="section-stack">
                    <div className="qp-regie-acting">
                        {live.actingTeamId != null
                            ? <>Am Zug: <strong>{teamName(live.actingTeamId)}</strong></>
                            : 'Setzrunde abgeschlossen'}
                    </div>

                    {/* Aktions-Log der aktuellen Setzrunde */}
                    {live.actionLog && live.actionLog.length > 0 && (
                        <div className="qp-regie-action-log">
                            {live.actionLog.map((entry, i) => (
                                <div key={i} className="qp-regie-action-log-entry">
                                    <span className="qp-regie-action-team">{teamName(entry.teamId)}</span>
                                    <span className="qp-regie-action-label">{formatAction(entry.action)}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Überspringen nur solange Runde noch läuft */}
                    {!roundComplete && (
                        <div className="button-row">
                            <Button variant="surface" size="sm" onClick={() => emit('quizpoker:skipBetting')}>
                                Setzrunde überspringen
                            </Button>
                        </div>
                    )}
                    <div className="qp-regie-override">
                        <span className="text-muted">Am Zug setzen:</span>
                        {teams.map((t) => (
                            <Button key={t.id} size="sm" variant="ghost" onClick={() => emit('quizpoker:setActingTeam', { teamId: t.id })}>
                                {t.name}
                            </Button>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Showdown: Team-Aufdeckung + Lösung ── */}
            {phase === 'showdown' && (
                <div className="section-stack">
                    <div className="qp-regie-reveal">
                        {perTeamIds
                            .filter((id) => live.perTeam[id].status !== 'folded')
                            .map((id) => (
                                <Button
                                    key={id}
                                    size="sm"
                                    variant={live.perTeam[id].revealed ? 'surface' : 'primary'}
                                    onClick={() => emit('quizpoker:revealTeam', { teamId: id, revealed: !live.perTeam[id].revealed })}
                                >
                                    {teamName(id)} {live.perTeam[id].revealed ? 'verbergen' : 'aufdecken'}
                                </Button>
                            ))}
                    </div>
                    <div className="button-row">
                        <Button
                            variant="surface"
                            onClick={() => emit('quizpoker:revealSolution', { revealed: !live.solutionRevealed })}
                        >
                            {live.solutionRevealed ? 'Lösung verbergen' : 'Lösung aufdecken'}
                        </Button>
                    </div>
                </div>
            )}

            {/* ── Ergebnis ── */}
            {phase === 'resolved' && live?.result && (
                <div className="qp-regie-result">
                    Gewinner: {live.result.winnerTeamIds.map((id) => teamName(id)).join(', ') || '—'}
                </div>
            )}

            {/* ── Korrekturen (ausklappbar) ── */}
            {live && (
                <div className="qp-regie-corrections">
                    <button
                        type="button"
                        className="qp-corrections-toggle text-muted"
                        onClick={() => setShowCorrections((v) => !v)}
                    >
                        {showCorrections ? '▾' : '▸'} Korrekturen
                    </button>

                    {showCorrections && (
                        <div className="section-stack qp-corrections-body">

                            {/* ── Team-Aktionen (Moderator-Override) ── */}
                            {phase === 'betting' && (
                                <div>
                                    <div className="text-muted" style={{ fontSize: '13px', marginBottom: '6px' }}>Team-Aktionen (Override)</div>
                                    {perTeamIds
                                        .filter((id) => live.perTeam[id].status === 'in')
                                        .map((id) => {
                                            const entry = live.perTeam[id];
                                            const team = teams.find((t) => String(t.id) === id);
                                            const stack = Math.max(0, (team?.punkte || 0) - entry.committedTotal);
                                            const cb = live.currentBet || 0;
                                            const toCall = Math.max(0, cb - entry.roundContribution);
                                            const canCheck = entry.roundContribution === cb;
                                            const raiseVal = Number(raiseInputs[id]) || (cb + 1);
                                            return (
                                                <div key={id} className="qp-correction-row" style={{ flexWrap: 'wrap', gap: '6px' }}>
                                                    <span className="qp-correction-label">{teamName(id)}</span>
                                                    {canCheck ? (
                                                        <Button size="sm" variant="ghost"
                                                            onClick={() => emit('quizpoker:moderatorBet', { teamId: id, action: 'check' })}>
                                                            Check
                                                        </Button>
                                                    ) : (
                                                        <Button size="sm" variant="ghost" disabled={toCall <= 0}
                                                            onClick={() => emit('quizpoker:moderatorBet', { teamId: id, action: 'call' })}>
                                                            Call {Math.min(toCall, stack)}
                                                        </Button>
                                                    )}
                                                    <input
                                                        type="number"
                                                        className="qp-correction-input"
                                                        style={{ width: 70 }}
                                                        placeholder={`${cb + 1}`}
                                                        value={raiseInputs[id] ?? ''}
                                                        onChange={(e) => setRaiseInputs((prev) => ({ ...prev, [id]: e.target.value }))}
                                                    />
                                                    <Button size="sm" variant="ghost"
                                                        disabled={stack <= 0}
                                                        onClick={() => emit('quizpoker:moderatorBet', { teamId: id, action: 'raise', amount: raiseVal })}>
                                                        Raise {raiseVal}
                                                    </Button>
                                                    <Button size="sm" variant="ghost"
                                                        onClick={() => emit('quizpoker:moderatorBet', { teamId: id, action: 'allin' })}>
                                                        All-In
                                                    </Button>
                                                    <Button size="sm" variant="danger"
                                                        onClick={() => emit('quizpoker:moderatorBet', { teamId: id, action: 'fold' })}>
                                                        Fold
                                                    </Button>
                                                </div>
                                            );
                                        })}
                                </div>
                            )}

                            {/* ── Schätzungen korrigieren ── */}
                            <div>
                                <div className="text-muted" style={{ fontSize: '13px', marginBottom: '6px' }}>Schätzungen</div>
                                <div className="qp-corrections-grid">
                                    {perTeamIds.map((id) => (
                                        <div key={id} className="qp-correction-row">
                                            <span className="qp-correction-label">{teamName(id)}</span>
                                            <input
                                                type="number"
                                                className="qp-correction-input"
                                                placeholder={live.perTeam[id].answer != null ? String(live.perTeam[id].answer) : '—'}
                                                value={answerInputs[id] ?? ''}
                                                onChange={(e) => setAnswerInputs((prev) => ({ ...prev, [id]: e.target.value }))}
                                            />
                                            <Button size="sm" variant="ghost" onClick={() => {
                                                emit('quizpoker:overrideAnswer', { teamId: id, value: answerInputs[id] });
                                                setAnswerInputs((prev) => ({ ...prev, [id]: '' }));
                                            }}>
                                                Setzen
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={() => {
                                                emit('quizpoker:overrideAnswer', { teamId: id, value: null });
                                                setAnswerInputs((prev) => ({ ...prev, [id]: '' }));
                                            }}>
                                                Löschen
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* ── Einsätze korrigieren ── */}
                            <div>
                                <div className="text-muted" style={{ fontSize: '13px', marginBottom: '6px' }}>Einsätze (Gesamt-Eingezahlt)</div>
                                <div className="qp-corrections-grid">
                                    {perTeamIds.map((id) => (
                                        <div key={id} className="qp-correction-row">
                                            <span className="qp-correction-label">{teamName(id)}</span>
                                            <input
                                                type="number"
                                                className="qp-correction-input"
                                                placeholder={String(live.perTeam[id].committedTotal ?? 0)}
                                                value={betInputs[id] ?? ''}
                                                onChange={(e) => setBetInputs((prev) => ({ ...prev, [id]: e.target.value }))}
                                            />
                                            <Button size="sm" variant="ghost" onClick={() => {
                                                emit('quizpoker:overrideTeamCommitted', { teamId: id, amount: betInputs[id] });
                                                setBetInputs((prev) => ({ ...prev, [id]: '' }));
                                            }}>
                                                Setzen
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* ── Pot direkt setzen ── */}
                            <div className="qp-correction-row">
                                <span className="qp-correction-label">Pot direkt</span>
                                <input
                                    type="number"
                                    className="qp-correction-input"
                                    placeholder={String(live.pot)}
                                    value={potInput}
                                    onChange={(e) => setPotInput(e.target.value)}
                                />
                                <Button size="sm" variant="ghost" onClick={() => {
                                    emit('quizpoker:overridePot', { amount: potInput });
                                    setPotInput('');
                                }}>
                                    Setzen
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default FinaleQuizpokerRegie;
